import { useGameStore } from '../store/gameStore';
import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { shareInvite, type ShareResult } from '../lib/invite';
import { trackEvent } from '../utils/analytics';
import { getGuestName } from '../lib/guestName';

// The waiting screen tells the truth about the queue. With a small player
// base the honest state is often "you are first in line", and a player left
// alone with a radar animation leaves the app. So: the real number of other
// people searching, and after ten seconds two ways out that keep them in the
// game (invite a friend to this very room, or play the same mode solo).
// No invented counts, ever.

const OFFER_AFTER_SECONDS = 10;
const GIVE_UP_AFTER_SECONDS = 60;

export function Lobby() {
  const inviteCode = useGameStore((s) => s.inviteCode);
  const gameMode = useGameStore((s) => s.gameMode);
  const roomId = useGameStore((s) => s.roomId);
  const nickname = useGameStore((s) => s.nickname);
  const isSolo = gameMode === 'solo';
  const isQuick = gameMode === 'quick_match';
  const resetGame = useGameStore((s) => s.resetGame);
  const setView = useGameStore((s) => s.setView);
  const checkForOpponent = useGameStore((s) => s.checkForOpponent);
  const [elapsed, setElapsed] = useState(0);
  const [othersWaiting, setOthersWaiting] = useState<number | null>(null);
  const [shareState, setShareState] = useState<ShareResult | null>(null);
  const startedAt = useRef(Date.now());

  // Live timer
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // This screen being on the player's display is the proof that they are still
  // waiting, so this is the one place a check for the opponent cannot fail to
  // be running. The store polls too, but that depends on initListeners having
  // set its interval up and kept it; this depends on nothing but the lobby
  // being visible. checkForOpponent returns immediately once a match is under
  // way, so the two overlapping polls cost a comparison.
  useEffect(() => {
    if (isSolo) return;
    void checkForOpponent();
    const interval = setInterval(() => { void checkForOpponent(); }, 1500);
    return () => clearInterval(interval);
  }, [isSolo, checkForOpponent]);

  // How many OTHER people are searching right now: fresh waiting RUSH rooms
  // that are not this one. Same definition quick match itself uses to join.
  useEffect(() => {
    if (!isQuick) return;
    let alive = true;
    const poll = async () => {
      try {
        const freshCutoff = new Date(Date.now() - 2 * 60 * 1000).toISOString();
        let q = supabase
          .from('matches')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'waiting')
          .eq('is_solo', false)
          .eq('mode', 'RUSH')
          .gte('created_at', freshCutoff);
        if (roomId) q = q.neq('id', roomId);
        const { count } = await q;
        if (alive) setOthersWaiting(count ?? 0);
      } catch { /* the number is a courtesy, never a blocker */ }
    };
    void poll();
    const interval = setInterval(poll, 5000);
    return () => { alive = false; clearInterval(interval); };
  }, [isQuick, roomId]);

  function leave(outcome: 'cancelled' | 'solo' | 'invited') {
    if (isQuick) trackEvent('quick_match_wait', { seconds: elapsed, others_waiting: othersWaiting, outcome });
  }

  function handleCancel() {
    leave('cancelled');
    resetGame();
    setView('home');
  }

  async function handleInvite() {
    if (!inviteCode) return;
    const result = await shareInvite(inviteCode, nickname || getGuestName());
    setShareState(result);
    trackEvent('invite_shared', { from: gameMode, result });
    if (isQuick) trackEvent('quick_match_wait', { seconds: elapsed, others_waiting: othersWaiting, outcome: 'invited' });
  }

  async function handlePlaySolo() {
    leave('solo');
    const st = useGameStore.getState();
    resetGame();
    st.setGameMode('solo');
    st.setSelectedMode('RUSH');
    st.setSelectedRealm('ALL');
    await useGameStore.getState().startSoloMatch('ALL');
  }

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const timeStr = `${mins}:${secs.toString().padStart(2, '0')}`;

  const offer = isQuick && elapsed >= OFFER_AFTER_SECONDS && (othersWaiting ?? 0) === 0;
  const gaveUp = isQuick && elapsed >= GIVE_UP_AFTER_SECONDS && (othersWaiting ?? 0) === 0;

  let queueLine = 'waiting for opponent...';
  if (isQuick && othersWaiting !== null) {
    queueLine = othersWaiting > 0
      ? (othersWaiting === 1 ? '1 player searching too' : `${othersWaiting} players searching too`)
      : 'You are first in line';
  }
  if (gaveUp) queueLine = 'Nobody else is searching';

  const inviteLabel = shareState === 'copied' ? 'LINK COPIED' : shareState === 'shared' ? 'INVITE SENT' : 'INVITE A FRIEND';

  return (
    <div className="page-lobby">

      {/* Friends' rooms: the code big, the link one tap away. */}
      {!isQuick && inviteCode && (
        <div className="lobby-invite">
          <span className="lobby-invite-label">ROOM CODE</span>
          <span className="room-code">{inviteCode}</span>
          <button className="btn btn-primary lobby-invite-btn" onClick={handleInvite} id="btn-invite">
            {inviteLabel}
          </button>
          <span className="lobby-hint">Share the link, or read the code out loud.</span>
        </div>
      )}

      {/* Radar rings */}
      <div className={`radar-container${offer ? ' radar-dim' : ''}`}>
        {Array.from({ length: 10 }).map((_, i) => <div key={i} className="radar-ring" />)}
        <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, zIndex: 2 }}>
          <span className="lobby-status">{isSolo ? 'LOADING' : 'SEARCHING'}</span>
          {!isSolo && <span className="lobby-queue">{queueLine}</span>}
        </div>
      </div>

      {/* Elapsed time */}
      {!isSolo && <span className="lobby-wait">{timeStr}</span>}

      {/* Quick match, nobody around: two ways to keep playing. The search
          keeps running underneath; a late opponent still gets matched. */}
      {offer && inviteCode && (
        <div className={`lobby-actions animate-in${gaveUp ? ' lobby-actions-strong' : ''}`}>
          <button className="btn btn-primary lobby-invite-btn" onClick={handleInvite} id="btn-invite-quick">
            {inviteLabel}
          </button>
          <button className="btn btn-ghost lobby-solo-btn" onClick={handlePlaySolo} id="btn-play-solo">
            PLAY RUSH SOLO INSTEAD
          </button>
          {!gaveUp && <span className="lobby-hint">We keep searching while you decide.</span>}
        </div>
      )}

      {/* Cancel */}
      <button className="btn btn-ghost" onClick={handleCancel} id="btn-cancel" style={{ fontSize: '0.95rem', letterSpacing: '0.15em' }}>
        CANCEL
      </button>
    </div>
  );
}
